import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2 } from "lucide-react";

/**
 * Google OAuth callback handler. Mobile-Safari-safe:
 * - Receives session_id in the URL hash from Emergent auth.
 * - POSTs to /api/auth/google/session; stores the returned JWT in
 *   localStorage BEFORE calling /auth/me so Safari ITP cookie drops cannot
 *   knock us back to login (api.js sends the Bearer header automatically).
 * - Keeps the hash in place until after the POST succeeds (prevents the
 *   api.js 401 interceptor from force-redirecting mid-flow).
 * - Shows a recoverable error with a Retry button instead of silently
 *   bouncing to /login on transient Safari failures.
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [err, setErr] = useState("");

  const run = async () => {
    setErr("");
    const fragment = new URLSearchParams((location.hash || window.location.hash || "").replace(/^#/, ""));
    const sessionId = fragment.get("session_id");
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }
    try {
      // Clear any stale Bearer so the backend doesn't match a wrong account.
      localStorage.removeItem("atl_token");
      localStorage.removeItem("atl_view_as");

      const { data } = await api.post(
        "/auth/google/session",
        null,
        { headers: { "X-Session-ID": sessionId } },
      );

      // Store the JWT BEFORE anything that might trigger /me. Safari ITP may
      // drop the SameSite=None cookie across the redirect — Bearer survives.
      if (data?.access_token) {
        localStorage.setItem("atl_token", data.access_token);
      }

      // Now that the token is persisted, scrub the hash so a hard refresh
      // doesn't re-run the exchange with a now-consumed session id.
      try { window.history.replaceState({}, "", window.location.pathname); } catch {}

      // Resolve the authenticated user. Retry once with a short delay to
      // tolerate any propagation lag on mobile.
      let u = await refresh();
      if (!u) {
        await new Promise((r) => setTimeout(r, 350));
        u = await refresh();
      }
      if (!u) {
        setErr("Signed in, but we couldn't load your profile. Tap Retry.");
        return;
      }

      const status = data?.status;
      if (status === "pending_signup") {
        navigate("/onboarding", { replace: true });
      } else if (status === "awaiting_approval" || (!u.approved && u.role !== "admin")) {
        navigate("/pending-approval", { replace: true });
      } else {
        navigate(u.onboarded ? "/" : "/onboarding", { replace: true });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Sign-in failed. Please try again.";
      setErr(detail);
    }
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    run();
    // eslint-disable-next-line
  }, []);

  const retry = () => {
    hasProcessed.current = true;
    run();
  };

  const backToLogin = () => {
    localStorage.removeItem("atl_token");
    try { window.history.replaceState({}, "", "/login"); } catch {}
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0A1F44] text-white flex items-center justify-center px-6">
      <div className="text-center max-w-sm" data-testid="auth-callback">
        {!err ? (
          <>
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#C9A961]" />
            <div className="mt-3 font-serif text-lg">Signing you in…</div>
          </>
        ) : (
          <>
            <div className="font-serif text-lg">Hmm, that didn't go through.</div>
            <div className="mt-2 text-sm text-[#F7C5C5]" data-testid="auth-callback-error">{err}</div>
            <div className="mt-4 flex items-center justify-center gap-2">
              <button
                onClick={retry}
                data-testid="auth-callback-retry"
                className="px-4 py-2 rounded-full bg-[#C9A961] text-[#0A1F44] text-sm font-semibold"
              >
                Retry
              </button>
              <button
                onClick={backToLogin}
                data-testid="auth-callback-back"
                className="px-4 py-2 rounded-full border border-white/30 text-white text-sm"
              >
                Back to login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
